import { ClientContext } from "ClientProvider";
import { getTransactionByGateway } from "api/graphQL_dashboard";
import PieChart from "components/charts/PieChart";
import DivLoader from "components/divloader/DivLoader";
import { useEffect, useState, useContext } from "react";
import { pieChartOptions as pieChartOptionsPayload } from "variables/charts";

function calculatePercentage(obj: Object) {
  const totalSum = Object.values(obj).reduce((sum: number, value: any) => sum + value, 0);
  if (totalSum === 0) return null;
  return Object.entries(obj)
    .map(([key, value]) => ({ key, value, original: value }))
    .filter((e) => e.value !== 0);
}

const COLORS = pieChartOptionsPayload?.colors ?? ["#2563eb","#16a34a","#dc2626","#d97706","#7c3aed","#0891b2"];

const TransactionPieChartCard = ({
  type, datesTop, timeFrameTop, selectOrg, selectClient, selectCard,
}: {
  type?: string; datesTop?: any; timeFrameTop?: any;
  selectOrg?: any; selectClient?: any; selectCard?: any;
}) => {
  const [pieChartData, setPieChartData] = useState<any>();
  const [pieChartOptions, setPieChartOptions] = useState<any>();
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const { dashboardData } = useContext(ClientContext);
  const [timeFrame, setTimeFrame] = useState<string>(timeFrameTop);
  const [dates, setDates] = useState<[Date, Date]>(datesTop);
  const isDecline = type === "decline";

  const fetchData = () => {
    setIsLoading(true);
    let data: any = {
      time_frame: timeFrame,
      org_ids: selectOrg?.length > 0 ? selectOrg?.map((m: any) => m?.id)?.toString() : "",
      client_ids: selectClient?.length > 0 ? selectClient?.map((m: any) => m?.id)?.toString() : "",
      card_type: selectCard?.length > 0 ? selectCard?.map((m: any) => m?.value)?.toString() : "",
    };
    if (dates) { data = { ...data, start_date: dates[0].toISOString().substring(0, 10), end_date: dates[1].toISOString().substring(0, 10) }; }
    if (timeFrame !== "custom") { delete data.start_date; delete data.end_date; }
    getTransactionByGateway(type, dashboardData, data)
      .then((d) => { setPieChartData(calculatePercentage(d)); setPieChartOptions({ ...pieChartOptionsPayload, labels: Object.keys(d) }); })
      .catch(() => {}).finally(() => setIsLoading(false));
  };

  useEffect(() => { const t = setTimeout(fetchData, 1000); return () => clearTimeout(t); }, [dashboardData, dates, timeFrame, selectOrg, selectClient, selectCard]);
  useEffect(() => { setTimeFrame(timeFrameTop); setDates(datesTop); }, [timeFrameTop, datesTop]);

  const total = pieChartData?.reduce((acc: number, d: any) => acc + d.original, 0) ?? 0;

  return (
    <div className="tpc-card">
      {/* ── Header ── */}
      <div className="tpc-header">
        <div className="tpc-header-left">
          <div className={`tpc-icon${isDecline ? " tpc-icon--red" : " tpc-icon--green"}`}>
            {isDecline ? (
              <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                <circle cx="12" cy="12" r="10"/><line x1="15" y1="9" x2="9" y2="15"/><line x1="9" y1="9" x2="15" y2="15"/>
              </svg>
            ) : (
              <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                <circle cx="12" cy="12" r="10"/><polyline points="9 12 11 14 15 10"/>
              </svg>
            )}
          </div>
          <div>
            <h3 className="tpc-title">{isDecline ? "Declined" : "Approved"} Transactions</h3>
            <p className="tpc-subtitle">By payment gateway</p>
          </div>
        </div>
        {!isLoading && total > 0 && (
          <div className={`tpc-total-badge${isDecline ? " tpc-total-badge--red" : " tpc-total-badge--green"}`}>
            {total.toLocaleString()} total
          </div>
        )}
      </div>

      {/* ── Body ── */}
      <div className={`tpc-body ${!pieChartData ? "tpc-body-empty" : ""}`}>
        {isLoading ? (
          <div className="tpc-center">
            <div className="tpc-spinner" />
            <p className="tpc-hint">Fetching data…</p>
          </div>
        ) : pieChartData && pieChartOptions ? (
          <>
            <div className="tpc-chart-wrap">
              <PieChart chartOptions={pieChartOptions} chartData={pieChartData.map((d: any) => d.original)} chartType="pie" />
            </div>
            {/* Custom legend */}
            <div className="tpc-legend">
              {pieChartData.map((d: any, i: number) => {
                const pct = total > 0 ? Math.round((d.original / total) * 100) : 0;
                return (
                  <div key={i} className="tpc-legend-row">
                    <div className="tpc-legend-dot" style={{ background: COLORS[i % COLORS.length] }} />
                    <span className="tpc-legend-key">{d.key}</span>
                    <span className="tpc-legend-val">{d.original.toLocaleString()}</span>
                    <div className="tpc-legend-bar-wrap">
                      <div className="tpc-legend-bar" style={{ width: `${pct}%`, background: COLORS[i % COLORS.length] }} />
                    </div>
                    <span className="tpc-legend-pct">{pct}%</span>
                  </div>
                );
              })}
            </div>
          </>
        ) : (
          <div className="tpc-center">
            <div className="tpc-empty-icon">
              <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                <path d="M21.21 15.89A10 10 0 1 1 8 2.83"/><path d="M22 12A10 10 0 0 0 12 2v10z"/>
              </svg>
            </div>
            <p className="tpc-empty-title">No data available</p>
            <p className="tpc-hint">No {isDecline ? "declined" : "approved"} transactions found for the selected filters</p>
          </div>
        )}
      </div>

      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Plus+Jakarta+Sans:wght@400;500;600;700;800&display=swap');

        .tpc-card {
          font-family: 'Plus Jakarta Sans', sans-serif;
          background: #ffffff;
          border: 1px solid #e2e8f0;
          border-radius: 18px;
          overflow: hidden;
          box-shadow: 0 2px 8px rgba(15,23,42,0.06);
          display: flex; flex-direction: column;
          height: 100%;
        }

        /* Header */
        .tpc-header {
          padding: 20px 22px 16px;
          border-bottom: 1px solid #f1f5f9;
          display: flex; align-items: center; justify-content: space-between; gap: 10px; flex-wrap: wrap;
        }
        .tpc-header-left { display: flex; align-items: center; gap: 12px; }
        .tpc-icon {
          width: 40px; height: 40px; border-radius: 11px;
          display: flex; align-items: center; justify-content: center; flex-shrink: 0;
        }
        .tpc-icon--green { background: #f0fdf4; border: 1.5px solid #bbf7d0; color: #16a34a; }
        .tpc-icon--red   { background: #fff7f7; border: 1.5px solid #fecaca; color: #dc2626; }
        .tpc-title { font-size: 14.5px; font-weight: 700; color: #0f172a; letter-spacing: -0.2px; margin-bottom: 2px; line-height:22px}
        .tpc-subtitle { font-size: 11.5px; color: #94a3b8; font-weight: 500; }

        .tpc-total-badge {
          font-size: 12px; font-weight: 700; padding: 5px 12px; border-radius: 100px;
          white-space: nowrap;
        }
        .tpc-total-badge--green { background: #f0fdf4; color: #15803d; border: 1px solid #bbf7d0; }
        .tpc-total-badge--red   { background: #fff7f7; color: #dc2626; border: 1px solid #fecaca; }

        /* Body */
        // .tpc-body { flex: 1; padding: 12px 18px 20px; display: flex; flex-direction: column; gap: 12px; }
        .tpc-body { display:grid;grid-template-columns:220px 1fr;gap:16px;padding:20px; }
        
        .tpc-chart-wrap { height: 220px; display: flex; align-items: center; justify-content: center; }
        .tpc-legend::-webkit-scrollbar { width: 4px; }
        .tpc-legend::-webkit-scrollbar-track { background: #f8fafc; }
        .tpc-legend::-webkit-scrollbar-thumb { background: #cbd5e1; border-radius: 4px; }
        /* Legend */
        .tpc-legend { display: flex; flex-direction: column; gap: 8px; max-height: 220px;overflow-y: auto; padding-right:5px }
        .tpc-legend-row { display: flex; align-items: center; gap: 8px; }
        .tpc-legend-dot { width: 8px; height: 8px; border-radius: 50%; flex-shrink: 0; }
        .tpc-legend-key { font-size: 12px; color: #475569; font-weight: 500; flex: 1; min-width: 0; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
        .tpc-legend-val { font-size: 12px; font-weight: 700; color: #0f172a; min-width: 48px; text-align: right; }
        .tpc-legend-bar-wrap { width: 60px; height: 5px; background: #f1f5f9; border-radius: 100px; overflow: hidden; flex-shrink: 0; }
        .tpc-legend-bar { height: 100%; border-radius: 100px; transition: width 0.4s ease; }
        .tpc-legend-pct { font-size: 11.5px; font-weight: 700; color: #64748b; min-width: 32px; text-align: right; }
        .tpc-body-empty { grid-template-columns: 1fr; }
        /* States */
        .tpc-center { display: flex; flex-direction: column; align-items: center; justify-content: center; flex: 1; gap: 10px; min-height: 200px; }
        .tpc-empty-icon { width: 52px; height: 52px; border-radius: 13px; background: #f8fafc; border: 1.5px solid #e2e8f0; display: flex; align-items: center; justify-content: center; color: #cbd5e1; }
        .tpc-empty-title { font-size: 13.5px; font-weight: 700; color: #64748b; }
        .tpc-hint { font-size: 12px; color: #94a3b8; font-weight: 500; text-align: center; max-width: 200px; }
        .tpc-spinner { width: 26px; height: 26px; border: 3px solid #e2e8f0; border-top-color: #2563eb; border-radius: 50%; animation: tpcSpin 0.7s linear infinite; }
        @keyframes tpcSpin { to { transform: rotate(360deg); } }
      `}</style>
    </div>
  );
};

export default TransactionPieChartCard;